<template>
	<!-- THIS :key="reRenderApp" SHOULD NEVER BE DELETED, IT RE-RENDERS THE APP WHEN SWITCHING ACCOUNTS -->
	<div :key="reRenderApp" class="app-container" :class="{ 'is-guided-tutorial-on': isGuidedTutorialOn }">
		<div class="main-navigation-container height-100" :class="{ collapsed: isSidebarCollapsed }">
			<AppMainNavigation />
		</div>
		<div class="display-flex flex-column flex-1">
			<div class="app-content-container height-100">
				<component v-if="pageHeaderComponent" :is="pageHeaderComponent" v-bind="pageHeaderComponentProps || {}" />
				<div class="app-content height-100" :class="{ 'has-header': pageHeaderComponent, 'no-padding': noContentPadding }">
					<router-view class="height-100" />
				</div>
			</div>
		</div>
		<InAppInjectables />
	</div>
</template>

<script>
import 'vanilla-cookieconsent/src/cookieconsent.js';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

import { waitUntil } from 'async-wait-until';
import isString from 'lodash/isString';
import { mapGetters, mapActions } from 'vuex';

import AppMainNavigation from './AppNavigation/AppMainNavigation';
import { Modes } from '@/common.js';
import { FeatureType } from '@/components/CreationWizard/Steps/ChooseProjectAndFeatureType/FeatureTypeSelector/feature-type-selector.const';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { PLACEMENT_MODE_EVERYTHING_NAME, ACCOUNT_CHANGE_REQUIRED_ROUTE_NAME } from '@/router/router.const.js';
import { activeAccountId } from '@/store/modules/account/getters';
import { useIsSidebarCollapsed } from '@/store/modules/appLayout/getters';
import { isAccountOwner, userProductHuntMetadata, userOnBoardingMetadata } from '@/store/modules/auth/getters';
import { isInAppHelpEnabled, isCanduEnabled } from '@/store/modules/featureFlags/getters';
import { isGuidedTutorialOn } from '@/store/modules/onboarding/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import IntercomService from '@/utils/IntercomService';
import { isNullOrUndefined } from '@/utils/helpers';
import { SLACK_PLACEMENT_ID } from '@/utils/workflow-tool-service.const';
import { SECONDARY_NAVIGATION_ITEMS, PRIMARY_NAVIGATION_ITEMS } from '@/views/AppLayout/AppNavigation/app-navigation.const.js';
import CreateAndManageService from '@/views/AppLayout/ManageAndCreate/create-and-manage-service.js';
import InAppInjectables from '@/views/common/InAppInjectables';
export default {
	name: 'AppLayout',
	components: {
		AppMainNavigation,
		InAppInjectables,
	},
	computed: {
		...mapGetters(['isLoggedIn', 'user', useIsSidebarCollapsed.fullPath, isInAppHelpEnabled.fullPath]),
		...mapGetters('main', ['appURL']),
		...mapGettersShorthand({
			isGuidedTutorialOn,
			userProductHuntMetadata,
			userOnBoardingMetadata,
			isAccountOwner,
			activeAccountId,
			isCanduEnabled,
		}),
		isSidebarCollapsed() {
			return this[useIsSidebarCollapsed.fullPath];
		},
		routeName() {
			return this.$route.meta.title ?? this.$route.name;
		},
		allRoutes() {
			return [...this.primaryNavItems, ...this.secondaryNavItems];
		},
		currentRouteDetails() {
			return this.allRoutes.find((route) => route.routeName === this.$route.name) || {};
		},
		pageHeaderComponent() {
			if (this.$route.meta?.noHeader) {
				return null;
			}
			return (
				this.currentRouteDetails.headerComponent || this.$route.meta?.headerComponent || this.allComponentParentsHeaderComponents.at(-1)
			);
		},
		pageHeaderComponentProps() {
			if (this.$route.meta?.noHeader) {
				return null;
			}
			return (
				this.currentRouteDetails.headerComponentProps?.(this.$route) ||
				this.$route.meta?.headerComponentProps?.(this.$route) ||
				this.allComponentParentsHeaderProps.at(-1)
			);
		},
		allComponentParentsHeaderComponents() {
			return this.$route.matched.map((parent) => parent?.meta?.headerComponent)?.filter((v) => v) || [];
		},
		allComponentParentsHeaderProps() {
			return this.$route.matched.map((parent) => parent?.meta?.headerComponentProps)?.filter((v) => v) || [];
		},
		noContentPadding() {
			return this.$route.meta?.noContentPadding || this.$route.matched.some((r) => r.meta.noContentPadding);
		},
		seenWelcomeToPlyDialog() {
			return this.userOnBoardingMetadata.seenWelcomeToPlyDialog;
		},
	},
	data: () => ({
		reRenderApp: 1,
		showNewKitDialog: false,
		primaryNavItems: PRIMARY_NAVIGATION_ITEMS,
		secondaryNavItems: SECONDARY_NAVIGATION_ITEMS,
		dontRedirectToPlacementModeRoutes: [PLACEMENT_MODE_EVERYTHING_NAME, ACCOUNT_CHANGE_REQUIRED_ROUTE_NAME],
	}),
	methods: {
		...mapActions(['selectedProject/selectProjectId']),
		updateIntercom() {
			if (!this[isInAppHelpEnabled.fullPath]) {
				IntercomService.update({ alignment: 'right' }, false);
			}
		},
		async waitForIntercom() {
			if (process.env.NODE_ENV === Modes.Production) {
				await waitUntil(() => IntercomService.loaded() && IntercomService.booted(), { timeout: 10000 });
				this.updateIntercom();
			}
		},
		openWelcomeToPlyDialog() {
			AppDialogManagerService.openWelcomeToPlyDialog();
		},
		askForCookieConsent() {
			const cookieConsent = window.initCookieConsent();
			cookieConsent.run({
				force_consent: true,
				languages: {
					en: {
						consent_modal: {
							title: 'We use cookies',
							description:
								'This website uses cookies to enhance your visit and provide you with information tailored to your interests. For more information on our use of cookies please see our <a href="https://info.ply.io/privacy" target="_blank">Privacy Notice.</a>',
							primary_btn: {
								text: 'Accept all',
								role: 'accept_all', // 'accept_selected' or 'accept_all'
							},
							// secondary_btn: {
							// 	text: 'Reject all',
							// 	role: 'accept_necessary', // 'settings' or 'accept_necessary'
							// },
						},
						settings_modal: {
							title: 'Cookie preferences',
							save_settings_btn: 'Save settings',
							accept_all_btn: 'Accept all',
							reject_all_btn: 'Reject all',
							close_btn_label: 'Close',
							cookie_table_headers: [],
							blocks: [],
						},
					},
				},
				current_lang: 'en',
				gui_options: {
					consent_modal: {
						layout: 'cloud', // box/cloud/bar
						position: 'bottom center', // bottom/middle/top + left/right/center
						transition: 'slide', // zoom/slide
						swap_buttons: false, // enable to invert buttons
					},
					settings_modal: {
						layout: 'box', // box/bar
						transition: 'slide', // zoom/slide
					},
				},
				//...
			});
		},
		initCandu() {
			const user = this.user;
			if (window.Candu && this.isCanduEnabled) {
				window.Candu.init({
					// Required: your client token
					clientToken: process.env.VUE_APP_CANDU_CLIENT_TOKEN,
					// Optional: provide a User Id
					userId: user.id,
					// Optional: Traits provide a way to pass information about your users for segmentation and variables
					traits: {
						email: user.email,
						name: `${user.first_name} ${user.last_name}`,
						role: user.profile?.account?.role,
						billing_level: user.profile?.account?.billing_level,
						timezone: user.profile?.account?.timezone,
					},
				});
			}
		},
	},
	mounted() {
		// Whether to open creation flow
		if (this.$route.query.newSlackFeature) {
			AppDialogManagerService.openCreationWizardDialog({
				initialState: { featureTypeId: FeatureType.Tool, toolPlacement: SLACK_PLACEMENT_ID },
			});
		}

		if (IntercomService.loaded()) {
			this.updateIntercom();
			const openChat = this.$route.query.openChat;
			if (openChat) {
				if (isString(openChat) && openChat.length > 1) {
					IntercomService.showNewMessage(openChat);
				} else {
					IntercomService.show();
				}
			}
		} else {
			setTimeout(() => {
				this.updateIntercom();
			}, 2500);
		}
		const shouldAskForCookie = !process.env.VUE_APP_TEST_MODE;
		if (shouldAskForCookie) {
			this.askForCookieConsent();
		}
		this.waitForIntercom();
		if (!this.seenWelcomeToPlyDialog && this.isAccountOwner) {
			this.openWelcomeToPlyDialog();
		}

		this.initCandu();
	},

	watch: {
		isCanduEnabled(newV) {
			if (newV) {
				this.initCandu();
			}
		},
		async activeAccountId(newV, oldV) {
			if (newV !== oldV) {
				setTimeout(() => {
					this.reRenderApp++;
				});
				const isPlacementMode = this.dontRedirectToPlacementModeRoutes.every((routeName) =>
					CreateAndManageService.isRouteOrChildOfRoute(this.$route, routeName)
				);
				if (!isPlacementMode && !isNullOrUndefined(oldV)) {
					// and it's not the first time we load the app
					this.$router.push({ name: PLACEMENT_MODE_EVERYTHING_NAME }); // go to placement mode
				}
			}
		},
		immediate: true,
	},
};
</script>

<style scoped lang="scss">
.app-container {
	$top-bar-height: 110px;

	display: flex;
	flex-direction: row;
	height: 100vh;
	width: 100vw;

	&.is-guided-tutorial-on {
		height: calc(100vh - #{$bottom-player-bar-height});
	}
	.main-navigation-container {
		transition: 0.3s;
		min-width: $sidebar-width;
		max-width: $sidebar-width;

		&.collapsed {
			transition: 0.3s;
			min-width: $collapsed-sidebar-width;
			max-width: $collapsed-sidebar-width;
		}
	}

	.app-content-container {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		max-width: 100%;
		overflow: hidden;
		z-index: 1;
		box-shadow: -5px 0px 6px #00000021;
		border-radius: 12px 0px 0px 12px;

		.app-content {
			flex: 1;
			display: flex;

			&:not(.no-padding) {
				padding: 0 3%;
			}
			flex-direction: column;
			&.has-header {
				max-height: calc(100% - #{$top-bar-height});
			}
		}
	}

	.app-content {
		position: relative; // so animation can work with position absolute on second'app-content'
	}

	.app-content-layout:nth-of-type(2) {
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
	}
}
</style>
